import './App.css';
import React, { useState, useEffect } from "react";
import { APIProvider } from '@vis.gl/react-google-maps';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Geolocation } from '@capacitor/geolocation';
import DoorHopperMap from './components/DoorHopperMap';
import Navigation from './components/Navigation';
import AppointmentsList from "./components/AppointmentsList";
import NotificationBar from './components/NotificationBar';
import ClientDetails from './components/ClientDetails';
import LoadingScreen from './components/LoadingScreen';
import { AppCTX } from './data/AppContext';
import Login from './components/Login';
import ProspectDetails from './components/ProspectsList.js';
import TeamSignUp from './components/TeamSignUp';
import SearchAppBar from './components/TopBar';
import Container from '@mui/material/Container';
import AddProspect from './components/AddProspect';
import LeadsDrawer from './components/LeadsDrawer';
import PropertyInsightsTable from './components/PropertyInsightsTable';

const DEFAULT_LOCATION = { lat: 37.7749, lng: -122.4194 }; // Fallback (San Francisco) for web testing

function App() {
  const [height, setHeight] = useState(window.innerHeight)
  const [width, setWidth] = useState(window.innerWidth)
  const [showDetails, setShowDetails] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [leadsDrawerOpen, setLeadsDrawerOpen] = useState(false); // NEW STATE

  // Location acquisition state
  const [initialLocation, setInitialLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState('unknown');
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // triggering new deployment 
  useEffect(() => {
    // Function to update the inner height state
    const handleResize = () => {
      setHeight(window.innerHeight);
      setWidth(window.innerWidth);
    };

    // Add an event listener for the resize event when the component mounts
    window.addEventListener('resize', handleResize);

    // Remove the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Get context values first
  const {
    newProspect,
    setNewProspect,
    leadType,
    prospects,
    setProspects,
    setLeadType,
    status,
    setStatus,
    user,
    isMapLoaded,
    openAddProspect,
    propertyInsights,
    propertyInsightsLoading,
    propertyInsightsError,
    skipTracingLoading,
    skipTracingStatus,
    // BatchData
    premiumData,
    premiumDataLoading,
    premiumDataError,
    fetchPremiumData,
  } = React.useContext(AppCTX);

  // Location acquisition logic - runs after user is fully authenticated AND registered
  useEffect(() => {
    let isMounted = true;

    const acquireLocation = async () => {
      // Only run location acquisition if user is fully authenticated AND has completed registration
      // This means both user.uid (authenticated) AND user.data (registered) must exist
      if (!user.uid || !user.data) {
        // User is either not authenticated or hasn't completed registration yet
        // Don't start location acquisition, but don't interfere with initial loading
        setIsLocationLoading(false);
        return;
      }

      // User is fully authenticated and registered - now we can acquire location
      console.log('📍 APP: User is fully authenticated and registered, starting location acquisition');
      setIsLocationLoading(true);

      try {
        console.log('📍 APP: Starting location acquisition');

        // Check current permissions
        const permissions = await Geolocation.checkPermissions();
        console.log('📍 APP: Current permissions:', permissions);

        if (permissions.location !== 'granted') {
          // Request permission directly - no custom UI before this
          console.log('📍 APP: Requesting location permission');
          const newPermissions = await Geolocation.requestPermissions();

          if (newPermissions.location !== 'granted') {
            if (isMounted) {
              setLocationPermission('denied');
              // Fallback to a default location so the app can proceed in web testing
              setInitialLocation(DEFAULT_LOCATION);
              setIsLocationLoading(false);
            }
            return;
          }
        }

        setLocationPermission('granted');

        // Get initial location
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        });

        if (!isMounted) return;

        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        console.log('📍 APP: Location acquired:', location);
        setInitialLocation(location);
        setIsLocationLoading(false);

      } catch (error) {
        if (!isMounted) return;
        console.error('📍 APP: Location acquisition failed:', error);
        setLocationError(error.message);
        setLocationPermission('denied');
        // Fallback to a default location so the app can proceed in web testing
        setInitialLocation(DEFAULT_LOCATION);
        setIsLocationLoading(false);
      }
    };

    // Only call acquireLocation if user is fully authenticated and registered
    if (user.uid && user.data) {
      acquireLocation();
    }

    return () => {
      isMounted = false;
    };
  }, [user.uid, user.data]);

  // Configure Status Bar for safe area handling
  useEffect(() => {
    const configureStatusBar = async () => {
      try {
        // Set status bar style to auto-detect best visibility
        await StatusBar.setStyle({ style: Style.Default });

        // Set a lighter background color for better text visibility
        await StatusBar.setBackgroundColor({ color: '#111111' });

        // Enable safe area handling
        await StatusBar.setOverlaysWebView({ overlay: false });
      } catch (error) {
        console.log('Status bar configuration not available on this platform:', error);
      }
    };

    configureStatusBar();
  }, []);

  // Auto-open drawer when property insights are loaded
  useEffect(() => {
    if (propertyInsights && leadType === 0) {
      setLeadsDrawerOpen(true);
    }
  }, [propertyInsights, leadType]);


  // Show loading screen during location acquisition OR when user is ready but location not acquired
  if (isLocationLoading || (user.uid && user.data && !initialLocation)) {
    return <LoadingScreen
      isLocationLoading={isLocationLoading}
      locationPermission={locationPermission}
      locationError={locationError}
    />;
  }

  // Show login screen if not authenticated
  if (user.uid == null) {
    return (
      <div style={{ height: '100%', width: '100%' }} className="App">
        <Login />
      </div>
    )
  }
  // Show registration screen if authenticated but not registered
  else if (user.uid != null && user.data == null) {
    return (
      <div style={{ height: '100%', width: '100%' }} className="App">
        <TeamSignUp />
      </div>
    )
  }
  // Show main app with map only when user is authenticated, registered, AND location is acquired
  else {
    return (
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} libraries={['drawing', 'places']}>
        <div style={{ height: '100%', width: '100%' }} className="App">
          {/* Render the map only after location is acquired */}
          <div style={
            {
              position: 'fixed',
              top: 0,
              zIndex: 1,
              width: '100vw'
            }
          }>
            <SearchAppBar />
          </div>
          <div style={{
            position: 'fixed',
            top: 0,
            width: width,
            height: '100%',
            zIndex: '0',
            marginTop: 50,
          }}>
            <DoorHopperMap
              prospects={prospects}
              apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
              setProspects={setProspects}
              leadType={leadType}
              newProspect={newProspect}
              setNewProspect={setNewProspect}
              showDetails={showDetails}
              setShowDetails={setShowDetails}
              selectedProspect={selectedProspect}
              setSelectedProspect={setSelectedProspect}
              initialLocation={initialLocation}
            />
          </div>
          <LeadsDrawer
            count={prospects.length}
            status={leadType}
            showBackdrop={leadType !== 0}
            open={leadsDrawerOpen}
            setOpen={setLeadsDrawerOpen}
          >
            {leadType === 1 && (
              <ProspectDetails
                prospects={prospects}
                showDetails={showDetails}
                setShowDetails={setShowDetails}
                selectedProspect={selectedProspect}
                setSelectedProspect={setSelectedProspect}
              />
            )}
            {leadType === 0 && (
              <PropertyInsightsTable
                propertyData={propertyInsights}
                loading={propertyInsightsLoading}
                error={propertyInsightsError}
                ownersWithPhones={newProspect?.owners}
                skipTracingLoading={skipTracingLoading}
                skipTracingStatus={skipTracingStatus}
                // BatchData Props
                premiumData={premiumData}
                premiumDataLoading={premiumDataLoading}
                premiumDataError={premiumDataError}
                fetchPremiumData={fetchPremiumData}
              />
            )}
          </LeadsDrawer>

          <div style={
            {
              zIndex: '3'
            }
          }>
            <Navigation
              setCurrentScreen={(newValue) => {
                setLeadType(newValue);
                if (newValue === 0 || newValue === 1 || newValue === 2) {
                  setLeadsDrawerOpen(true);
                } else {
                  setLeadsDrawerOpen(false);
                }
              }}
              currentScreen={leadType}
            />
          </div>
          <div style={
            {
              position: 'fixed',
              bottom: 0,
              left: 40,
              zIndex: '4'
            }
          }>
            {status.notificationType !== 'neutral' ? <NotificationBar setStatus={setStatus} status={status} /> : null}
          </div>
          <ClientDetails
            open={showDetails}
            selectedProspect={selectedProspect}
            setSelectedProspect={setSelectedProspect}
            setShowDetails={setShowDetails}
          />
          <AddProspect />
        </div>
      </APIProvider>
    );
  }
}

export default App;
